import { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Avatar } from "@/components/common/Avatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Layers, Plus, ChevronDown, ChevronRight, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const PRIORITIES = ["low", "medium", "high", "critical"];

export default function EpicPlanning() {
  const { project } = useOutletContext();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { me, can } = useApp();
  const [adding, setAdding] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [form, setForm] = useState({ title: "", description: "", target_release: "", priority: "medium", assignee_id: "", source_requirement_id: "" });

  const { data: epics, isLoading } = useQuery({ queryKey: ["planning-epics", project.id], queryFn: () => get(`/pm/projects/${project.id}/planning/epics`) });
  const { data: allItems = [] } = useQuery({ queryKey: ["items", project.id], queryFn: () => get(`/pm/projects/${project.id}/items`) });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const { data: client } = useQuery({ queryKey: ["client", project.client_id], queryFn: () => get(`/crm/clients/${project.client_id}`), enabled: !!project.client_id });
  const { data: requirements = [] } = useQuery({
    queryKey: ["lead-requirements", client?.source_lead_id],
    queryFn: () => get(`/crm/leads/${client.source_lead_id}/requirements`),
    enabled: !!client?.source_lead_id,
  });

  const create = useMutation({
    mutationFn: (body) => post(`/pm/projects/${project.id}/items`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["planning-epics", project.id] }); qc.invalidateQueries({ queryKey: ["items", project.id] }); toast.success("Epic created"); setAdding(false); setForm({ title: "", description: "", target_release: "", priority: "medium", assignee_id: "", source_requirement_id: "" }); },
  });

  const modulesOf = (epicId) => allItems.filter((i) => i.parent_id === epicId && i.type === "Module");

  return (
    <div>
      <PageHeader title="Epic Planning" subtitle="Roadmap-level breakdown for this project.">
        {can("pm", "create") && (
          <Button data-testid="add-epic-btn" onClick={() => setAdding((s) => !s)}><Plus className="mr-1.5 h-4 w-4" />New Epic</Button>
        )}
      </PageHeader>

      {isLoading ? <Skeleton className="h-64" /> : (
        <Section>
          {adding && (
            <div className="border-b border-border/60 bg-muted/30 p-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Title *</Label><Input data-testid="epic-title" className="mt-1" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} /></div>
                <div className="col-span-2"><Label>Description</Label><Textarea data-testid="epic-description" className="mt-1" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
                <div><Label>Target Release / Quarter</Label><Input data-testid="epic-target_release" placeholder="e.g. Q1 2027" className="mt-1" value={form.target_release} onChange={(e) => setForm((f) => ({ ...f, target_release: e.target.value }))} /></div>
                <div>
                  <Label>Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                    <SelectTrigger className="mt-1" data-testid="epic-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Epic Owner</Label>
                  <Select value={form.assignee_id} onValueChange={(v) => setForm((f) => ({ ...f, assignee_id: v }))}>
                    <SelectTrigger className="mt-1" data-testid="epic-owner"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {requirements.length > 0 && (
                  <div className="col-span-2">
                    <Label>Link to CRM Requirement (optional)</Label>
                    <Select value={form.source_requirement_id} onValueChange={(v) => setForm((f) => ({ ...f, source_requirement_id: v }))}>
                      <SelectTrigger className="mt-1" data-testid="epic-requirement"><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent>{requirements.map((r) => <SelectItem key={r.id} value={r.id}>{r.description}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
                <Button size="sm" data-testid="epic-submit" onClick={() => {
                  if (!form.title) { toast.error("Title is required"); return; }
                  create.mutate({ ...form, type: "Epic", in_planning: true, assignee_id: form.assignee_id || null, source_requirement_id: form.source_requirement_id || null });
                }}>Create</Button>
              </div>
            </div>
          )}
          {!epics?.length ? <EmptyState icon={Layers} title="No epics yet" description="Create an epic to start planning this project's roadmap." /> : (
            <div className="divide-y divide-border">
              {epics.map((ep) => {
                const mods = modulesOf(ep.id);
                const isOpen = expanded[ep.id];
                return (
                  <div key={ep.id}>
                    <div className="flex items-center gap-3 px-4 py-3 text-sm">
                      <button onClick={() => setExpanded((s) => ({ ...s, [ep.id]: !s[ep.id] }))} className="text-muted-foreground hover:text-foreground">
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                      <button className="flex-1 text-left font-medium hover:underline" onClick={() => navigate(`/pm/projects/${project.id}/items/${ep.id}`)}>{ep.title}</button>
                      <span className="w-24 text-xs text-muted-foreground">{ep.target_release || "—"}</span>
                      <StatusBadge status={ep.priority} />
                      <Avatar user={{ name: ep.owner_name }} size="xs" />
                      <span className="w-20 text-right text-xs text-muted-foreground">{ep.rolled_up_estimated_hours}h</span>
                      <div className="flex w-28 items-center gap-1.5">
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted"><div className="h-full bg-primary" style={{ width: `${ep.rolled_up_progress_pct}%` }} /></div>
                        <span className="text-xs text-muted-foreground">{ep.rolled_up_progress_pct}%</span>
                      </div>
                      <StatusBadge status={ep.column_id?.includes("col-done") ? "done" : "active"} />
                      <Button size="sm" variant="ghost" data-testid={`epic-modules-${ep.id}`} onClick={() => navigate(`/pm/projects/${project.id}/planning/modules?epic=${ep.id}`)}>
                        Modules <ArrowRight className="ml-1 h-3.5 w-3.5" />
                      </Button>
                    </div>
                    {isOpen && (
                      <div className="bg-muted/20 px-4 py-2 pl-11">
                        {mods.length === 0 ? <p className="py-1 text-xs text-muted-foreground">No modules yet.</p> : (
                          <div className="space-y-1">
                            {mods.map((m) => <div key={m.id} className="flex items-center gap-2 py-1 text-xs text-muted-foreground"><Layers className="h-3 w-3" />{m.title}</div>)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Section>
      )}
    </div>
  );
}
